import {
  deleteObject,
  getDownloadURL,
  ref,
  uploadBytesResumable,
} from "firebase/storage";
import { useCallback, useState } from "react";

import { storage } from "integrations/firebase/storage";

export const MAX_AVATAR_BYTES = 2 * 1024 * 1024;

// Exactly what `isImage()` in storage.rules admits. Anything else is refused
// there as storage/unauthorized, which reads as a permissions problem.
export const AVATAR_CONTENT_TYPES = ["image/png", "image/jpeg", "image/webp"];

const EXTENSIONS: Record<string, string> = {
  "image/png": "png",
  "image/jpeg": "jpg",
  "image/webp": "webp",
};

export function avatarPrefix(uid: string): string {
  return `users/${uid}/avatar/`;
}

/**
 * Why this file cannot be a profile photo, or null when it can.
 */
export function avatarFileError(file: File): string | null {
  if (!AVATAR_CONTENT_TYPES.includes(file.type)) {
    return "Choose a PNG, JPG or WebP image.";
  }
  if (file.size > MAX_AVATAR_BYTES) {
    return "Image must be under 2 MB.";
  }
  return null;
}

/**
 * A fresh object name per upload. Reusing the file's own name would overwrite
 * a same-named photo in place, and a browser that cached the old download URL
 * would keep showing it.
 */
export function avatarObjectPath(uid: string, file: File, now = Date.now()) {
  return `${avatarPrefix(uid)}${now}.${EXTENSIONS[file.type] ?? "img"}`;
}

/**
 * Upload a profile photo, with its progress.
 *
 * Nothing here deletes a photo the profile has held. Its URL is copied onto
 * every attendee row linked to the account, onto a speaker card an organiser
 * linked to one of those rows, and from there onto agenda sessions, and some
 * of those copies are never rewritten. A replaced or removed photo therefore
 * stays in Storage until the account is deleted, which removes everything
 * under `users/{uid}/`. At 2 MB a photo that costs little, and it keeps every
 * copy working.
 */
export function useAvatarUpload(uid: string) {
  const [uploading, setUploading] = useState(false);
  // 0 to 100 while an upload runs, null otherwise.
  const [progress, setProgress] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const upload = useCallback(
    async (file: File): Promise<string | null> => {
      setError(null);
      const invalid = avatarFileError(file);
      if (invalid) {
        setError(invalid);
        return null;
      }

      setUploading(true);
      setProgress(0);
      try {
        const storageRef = ref(storage, avatarObjectPath(uid, file));
        const task = uploadBytesResumable(storageRef, file, {
          contentType: file.type,
        });
        task.on("state_changed", (snapshot) => {
          if (snapshot.totalBytes > 0) {
            setProgress(
              Math.round(
                (snapshot.bytesTransferred / snapshot.totalBytes) * 100,
              ),
            );
          }
        });
        await task;
        return await getDownloadURL(storageRef);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Upload failed");
        return null;
      } finally {
        setUploading(false);
        setProgress(null);
      }
    },
    [uid],
  );

  /**
   * Delete a photo that was uploaded but never saved to the profile, because
   * saving it failed. Nothing can have copied its URL yet, which is the only
   * reason deleting it is safe. Best effort: a leftover costs storage and goes
   * with the account.
   */
  const discardUnsaved = useCallback(
    async (url: string): Promise<void> => {
      try {
        const unsaved = ref(storage, url);
        if (!unsaved.fullPath.startsWith(avatarPrefix(uid))) return;
        await deleteObject(unsaved);
      } catch {
        // Already gone, or refused. Nothing to undo.
      }
    },
    [uid],
  );

  return { upload, discardUnsaved, uploading, progress, error };
}
